import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TourPackage {
  id: string;
  tour_id: string;
  package_name: string;
  package_price: number;
  persons_included: number;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const useTourPackages = (tourId?: string) => {
  const [packages, setPackages] = useState<TourPackage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tourId) {
      fetchPackages(tourId);
    } else {
      setLoading(false);
    }
  }, [tourId]);

  const fetchPackages = async (id: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tour_packages')
        .select('*')
        .eq('tour_id', id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching tour packages:', error);
        return;
      }

      setPackages(data || []);
    } catch (error) {
      console.error('Error fetching tour packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const createPackage = async (packageData: Omit<TourPackage, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('tour_packages')
        .insert(packageData)
        .select()
        .single();

      if (error) {
        console.error('Error creating package:', error);
        throw error;
      }

      if (packageData.tour_id && data) {
        await fetchPackages(packageData.tour_id);
      }
      return data;
    } catch (error) {
      console.error('Error creating package:', error);
      throw error;
    }
  };

  const updatePackage = async (id: string, packageData: Partial<TourPackage>) => {
    try {
      const { data, error } = await supabase
        .from('tour_packages')
        .update(packageData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating package:', error);
        throw error;
      }

      if (tourId) {
        await fetchPackages(tourId);
      }
      return data;
    } catch (error) {
      console.error('Error updating package:', error);
      throw error;
    }
  };

  const deletePackage = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tour_packages')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting package:', error);
        throw error;
      }

      if (tourId) {
        await fetchPackages(tourId);
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      throw error;
    }
  };

  const getMinPrice = () => {
    if (packages.length === 0) return 0;
    return Math.min(...packages.map(pkg => pkg.package_price));
  };

  return {
    packages,
    loading,
    createPackage,
    updatePackage,
    deletePackage,
    refetch: tourId ? () => fetchPackages(tourId) : () => {},
    getMinPrice
  };
};